import {
  ProjectCriticalityResult,
  ProjectDto,
  ProjectLifecycle,
  ProjectStatus,
} from "../features/tasks/project/project.types";
import { urgencyFormula } from "./urgencyFormula";
import { getStartOfDayLocal } from "./timeParsing";

// ─── Helpers ────────────────────────────────────────────────────────────────

function daysBetween(a: Date, b: Date): number {
  return (b.getTime() - a.getTime()) / (1000 * 60 * 60 * 24);
}

// Whole calendar days, independent of the time of day (round absorbs DST shifts)
function calendarDaysBetween(a: Date, b: Date): number {
  return Math.round(daysBetween(getStartOfDayLocal(a), getStartOfDayLocal(b)));
}

function clamp(value: number, min = 0, max = 1): number {
  return Math.min(max, Math.max(min, value));
}

// ─── Color ──────────────────────────────────────────────────────────────────

export function getProjectColorByRating(num: number): string {
  const step = Math.min(10, Math.max(0, Math.round(num / 10)));
  const index = clamp(10 - step, 1, 9);
  return `var(--color__scale_${index})`;
}

/**
 * Accent color for a project: green only means "done", and without tasks or a
 * due date there is nothing to rate, so those stay neutral.
 */
/** Status set by date: completed / cancelled, otherwise open */
export function getProjectLifecycle(project: {
  completedAt?: string | Date | null;
  cancelledAt?: string | Date | null;
}): ProjectLifecycle {
  if (project.completedAt) return "completed";
  if (project.cancelledAt) return "cancelled";
  return "open";
}

/** Completed or cancelled projects are read-only until reopened */
export const isProjectClosed = (project?: {
  completedAt?: string | Date | null;
  cancelledAt?: string | Date | null;
}) => !!project && getProjectLifecycle(project) !== "open";

/**
 * Not on course, in the same sense as a goal "at risk" / "off track": late,
 * behind the plan, blocked or stalled. Used for role summaries.
 */
export const isProjectOffCourse = (status: ProjectStatus) =>
  ["Overdue", "Critical", "Blocked", "Off-track", "Stalled"].includes(status);

export function getProjectAccentColor(
  status: ProjectStatus,
  criticality: number,
  totalNotes: number,
): string {
  if (status === "Finished") return "var(--color__green500)";
  if (
    totalNotes === 0 ||
    status === "No time pressure" ||
    status === "Cancelled"
  )
    return "var(--color__grey400)";
  return getProjectColorByRating(criticality * 10 || 0);
}

// ─── Status ─────────────────────────────────────────────────────────────────

export function getProjectStatus(project: ProjectDto): {
  status: ProjectStatus;
} {
  const today = new Date();
  const totalNotes = project.finishedNotes + project.unfinishedNotes;
  const completionRatio = project.finishedNotes / totalNotes;

  // Layer 0: an explicit end beats everything that is computed
  const lifecycle = getProjectLifecycle(project);
  if (lifecycle === "completed") return { status: "Finished" };
  if (lifecycle === "cancelled") return { status: "Cancelled" };

  // Guard clauses
  if (project.startDate && new Date(project.startDate) > today)
    return { status: "Upcoming" };

  if (totalNotes === 0) return { status: "Empty" };

  if (
    project.dueDate &&
    new Date(project.dueDate) < today &&
    project.unfinishedNotes > 0
  )
    return { status: "Overdue" };

  if (completionRatio === 1) return { status: "Finished" };

  if (!project.dueDate) return { status: "No time pressure" };

  // Layer 1: Core variables
  const daysRemaining = daysBetween(today, new Date(project.dueDate));

  const totalDuration = project.startDate
    ? daysBetween(new Date(project.startDate), new Date(project.dueDate))
    : null;

  const urgencyFromFormula = urgencyFormula(daysRemaining) / 100;
  const urgencyFromTimeline = totalDuration
    ? clamp(1 - daysRemaining / totalDuration)
    : urgencyFromFormula;
  const urgency = urgencyFromTimeline;

  const daysSinceLastTask = project.lastFinishedNoteDate
    ? daysBetween(new Date(project.lastFinishedNoteDate), today)
    : null;

  const stalledThreshold = totalDuration
    ? Math.max(7, totalDuration * 0.15)
    : 7;
  const isStalled =
    daysSinceLastTask !== null && daysSinceLastTask > stalledThreshold;

  // Layer 2: Status tree
  if (isStalled && urgency > 0.5) return { status: "Blocked" };

  if (urgency > 0.6 && completionRatio < 0.4) return { status: "Critical" };

  if (urgency > 0.2 && completionRatio < urgency)
    return { status: "Off-track" };

  if (completionRatio > urgency) return { status: "Ahead of schedule" };

  if (isStalled && urgency < 0.5) return { status: "Stalled" };

  return { status: "Stable" };
}

// ─── Criticality ────────────────────────────────────────────────────────────

export function getProjectCriticality(
  project: ProjectDto,
): ProjectCriticalityResult {
  const today = new Date();
  const totalNotes =
    (project.finishedNotes ?? 0) + (project.unfinishedNotes ?? 0);

  const notYetStarted =
    project.startDate && new Date(project.startDate) > today;

  const completionRatio = (project.finishedNotes ?? 0) / totalNotes;

  const daysRemaining = project.dueDate
    ? calendarDaysBetween(today, new Date(project.dueDate))
    : null;

  const totalDuration =
    project.startDate && project.dueDate
      ? daysBetween(new Date(project.startDate), new Date(project.dueDate))
      : undefined;

  const urgencyFromFormula =
    !notYetStarted && daysRemaining !== null
      ? urgencyFormula(daysRemaining) / 100
      : 0;

  const urgencyFromTimeline =
    totalDuration && daysRemaining !== null
      ? clamp((totalDuration - daysRemaining) / totalDuration)
      : urgencyFromFormula;

  const urgency = notYetStarted ? 0 : urgencyFromTimeline;

  const calcUrgency = notYetStarted ? 0 : Math.max(urgency, urgencyFromFormula);

  const lag = Math.max(0, urgency - completionRatio);
  const amplifiedLag = Math.round(lag * (1 + calcUrgency) * 10 * 2);
  const hasNotStarted =
    project.startDate && new Date(project.startDate) > today;

  // Nothing left to rate once the project has been closed
  const criticality =
    hasNotStarted || isProjectClosed(project)
      ? 0
      : totalNotes === 0
        ? 0
        : clamp(amplifiedLag, 0, 100);

  return {
    criticality,
    urgency,
    daysRemaining,
    status: getProjectStatus(project).status,
    totalDuration,
    lag,
  };
}
